import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Button, Portal } from '@mui/material';
import { CloudUpload, Plus, Edit, Send, Trash2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTrackerLists, fetchTrackerDetails, manageTrackerList, sendTrackerForApproval, importTrackerExcel, setSearchFilters } from '../../redux/affiliateTrackerSlice';
import { selectEntityType } from '../../redux/authSlice';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import { ApiCall } from '../../helper/axios';
import axios from 'axios';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import TableToolbar from '../../common_components/TableToolbar';
import useToast from '../../common_components/Toast/useToast';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
// Refactored modular components
import UploadTrackerDrawer from './components/UploadTrackerDrawer';
import NotesModal from '../../common_components/NotesModal';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import { ROUTES } from '../../routes/routes.constants';
import { useNavigate } from 'react-router-dom';
// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtDate = (dateStr) => {
    if (!dateStr)
        return '—';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        });
    }
    catch {
        return dateStr;
    }
};
export default function PublisherTracker({ clientName = '' }) {
    const dispatch = useDispatch();
    const toast = useToast();
    const navigate = useNavigate();
    // Context selection
    const { selectedClientId } = useAccountOverview();
    // Redux State
    const { lists, currentListDetails, loading } = useSelector((state) => state.affiliateTracker);
    const token = useSelector((state) => state.auth?.token || '');
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    const permissions = useSelector((state) => state.auth.permissions);
    const affiliateActions = permissions?.affiliate_account_performance?.actions || [];
    const hasManageTrackersAccess = affiliateActions.includes('Manage Trackers');
    const hasClientApprovalAccess = affiliateActions.includes('Client Approval Access');
    // Component Local View State
    const [activeView, setActiveView] = useState('list');
    const [selectedTracker, setSelectedTracker] = useState(null);
    // Pagination & Sorting States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchText, setSearchText] = useState('');
    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, selectedClientId]);
    // Actions Menu (Vertical Ellipsis Dropdown) State
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [activeMenuTracker, setActiveMenuTracker] = useState(null);
    // Modals & Drawer States
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [notesTracker, setNotesTracker] = useState(null);
    const [notesValue, setNotesValue] = useState('');
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    // Auto-Fetch States
    const [autoFetchLoading, setAutoFetchLoading] = useState(false);
    // Dropdown Data States
    const [systemUsers, setSystemUsers] = useState([]);
    const [clientsList, setClientsList] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    // Manual Tracker Form State (Used during Editing)
    const [trackerName, setTrackerName] = useState('');
    const [manualSearchTerm, setManualSearchTerm] = useState('');
    const [manualDomain, setManualDomain] = useState('');
    const [manualArticle, setManualArticle] = useState('');
    const [manualTraffic, setManualTraffic] = useState('');
    const [manualRank, setManualRank] = useState('');
    const [manualNotes, setManualNotes] = useState('');
    const [primaryFirstName, setPrimaryFirstName] = useState('');
    const [primaryLastName, setPrimaryLastName] = useState('');
    const [primaryEmail, setPrimaryEmail] = useState('');
    const [manualStatus, setManualStatus] = useState('Pending');
    // Upload Form State
    const [uploadFile, setUploadFile] = useState(null);
    // Sync context selection & sorting to Redux filter and fetch
    useEffect(() => {
        dispatch(setSearchFilters({ selectedClientId, sortBy, sortOrder }));
        dispatch(fetchTrackerLists());
    }, [selectedClientId, sortBy, sortOrder, dispatch]);
    // Fetch system users for Change Owner dropdown & Clients list
    const loadDropdownData = useCallback(async () => {
        if (!token)
            return;
        try {
            const usersRes = await ApiCall('GET', '/api/users?limit=100', undefined, { Authorization: token });
            if (usersRes && 'data' in usersRes && usersRes.data?.success) {
                setSystemUsers(usersRes.data?.data?.users || []);
            }
            const clientsRes = await ApiCall('GET', '/api/visible-clients', undefined, { Authorization: token });
            if (clientsRes && 'data' in clientsRes && clientsRes.data?.success) {
                setClientsList(clientsRes.data?.data || []);
            }
        }
        catch (err) {
            console.error('Failed to load dropdown users/clients:', err);
        }
    }, [token]);
    useEffect(() => {
        loadDropdownData();
    }, [loadDropdownData]);
    // Actions Popover Handlers
    const handleMenuOpen = (event, tracker) => {
        setMenuAnchor(event.currentTarget);
        setActiveMenuTracker(tracker);
    };
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    // Helper to clear form
    const resetForm = () => {
        setTrackerName('');
        setManualSearchTerm('');
        setManualDomain('');
        setManualArticle('');
        setManualTraffic('');
        setManualRank('');
        setManualNotes('');
        setPrimaryFirstName('');
        setPrimaryLastName('');
        setPrimaryEmail('');
        setManualStatus('Pending');
    };
    // Fetch Details & Open Edit modal
    const handleEditClick = () => {
        handleMenuClose();
        if (activeMenuTracker) {
            navigate(`${ROUTES.addOutreachTracker.path}?id=${activeMenuTracker.id}&from=publisher_tracker`, {
                state: {
                    trackerId: activeMenuTracker.id,
                    trackerListName: activeMenuTracker.name,
                    clientName: clientName,
                    from: 'publisher_tracker'
                }
            });
        }
    };
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!activeMenuTracker)
            return;
        try {
            setActionLoading(true);
            const details = await dispatch(fetchTrackerDetails({ id: activeMenuTracker.id })).unwrap();
            const primaryContact = Array.isArray(details) ? details[0] : (details?.contacts?.[0] || null);
            if (!primaryContact) {
                toast.error('Primary contact not found. Please check the contact details and try again.');
                return;
            }
            const payload = {
                id: primaryContact.id,
                data: {
                    name: trackerName.trim(),
                    search_term: manualSearchTerm.trim(),
                    domain: manualDomain.trim(),
                    article_link: manualArticle.trim(),
                    traffic: Number(manualTraffic) || 0,
                    rank: Number(manualRank) || 0,
                    notes: manualNotes.trim(),
                    contacts: [
                        {
                            first_name: primaryFirstName.trim(),
                            last_name: primaryLastName.trim(),
                            primary_email: primaryEmail.trim(),
                            status: manualStatus
                        }
                    ]
                }
            };
            await dispatch(manageTrackerList({ action: 'update', payload })).unwrap();
            toast.success('Outreach tracker updated successfully.');
            setEditModalOpen(false);
            resetForm();
            dispatch(fetchTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to update tracker.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // Excel Upload Trigger & Submit
    const handleUploadButtonClick = () => {
        setUploadFile(null);
        setUploadDrawerOpen(true);
    };
    const handleExcelUploadSubmit = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            toast.error('Please select an Excel/CSV sheet.');
            return;
        }
        try {
            setActionLoading(true);
            const formData = new FormData();
            formData.append('file', uploadFile);
            if (selectedClientId) {
                formData.append('client_id', selectedClientId);
            }
            await dispatch(importTrackerExcel(formData)).unwrap();
            toast.success('Spreadsheet data imported successfully.');
            setUploadDrawerOpen(false);
            setUploadFile(null);
            dispatch(fetchTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to import Excel data.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // Send for Approval
    const handleSendApproval = async () => {
        if (!activeMenuTracker)
            return;
        const isResend = activeMenuTracker.hasApprovalSent;
        handleMenuClose();
        try {
            setActionLoading(true);
            await dispatch(sendTrackerForApproval({ tracker_id: activeMenuTracker.id })).unwrap();
            toast.success(isResend
                ? 'Resent tracker list for approval successfully.'
                : 'Sent tracker list for approval successfully.');
            dispatch(fetchTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to send for approval.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // Notes Handlers
    const handleNotesClick = (row) => {
        setNotesTracker(row);
        setNotesValue(row.notes || '');
        setNotesModalOpen(true);
    };
    const handleNotesSave = async (newNotes) => {
        if (!notesTracker)
            return;
        try {
            setActionLoading(true);
            const payload = {
                id: notesTracker.id,
                isList: true,
                data: {
                    notes: newNotes.trim()
                }
            };
            await dispatch(manageTrackerList({ action: 'update', payload })).unwrap();
            toast.success('Notes updated successfully.');
            setNotesModalOpen(false);
            dispatch(fetchTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to update notes.');
            throw err;
        }
        finally {
            setActionLoading(false);
        }
    };
    // Soft Delete Tracker List
    const handleDeleteClick = () => {
        if (!activeMenuTracker)
            return;
        handleMenuClose();
        setDeleteModalOpen(true);
    };
    const confirmDeleteTracker = async () => {
        if (!activeMenuTracker)
            return;
        try {
            setActionLoading(true);
            await dispatch(manageTrackerList({
                action: 'delete',
                payload: { id: activeMenuTracker.id, isList: true }
            })).unwrap();
            toast.success('Outreach tracker deleted successfully.');
            setDeleteModalOpen(false);
            dispatch(fetchTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to delete tracker.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // Auto-Fetch Contact for All Incomplete Records
    const handleBulkAutoFetch = async () => {
        if (currentListDetails.length === 0) {
            toast.warning('No records found in the current list.');
            return;
        }
        setAutoFetchLoading(true);
        try {
            const response = await axios.post('/api/affiliate-tracker/bulk-fetch-contact', {
                records: currentListDetails
            });
            if (response.data?.success) {
                const { updated_count, failed_count } = response.data?.data || {};
                toast.success(`Auto-fetch completed! ${updated_count} records updated, ${failed_count} failed.`);
                // Refresh the list
                dispatch(fetchTrackerLists());
            }
            else {
                toast.error(response.data?.message || 'Failed to auto-fetch contact information.');
            }
        }
        catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to auto-fetch contacts.';
            toast.error(errorMsg);
        }
        finally {
            setAutoFetchLoading(false);
        }
    };
    // Subview Navigation
    const handleViewDetails = (tracker) => {
        navigate(`/outreach-tracker/${tracker.id}?id=${tracker.id}&from=publisher_tracker`, {
            state: {
                trackerId: tracker.id,
                trackerListName: tracker.name,
                clientName: clientName,
                from: 'publisher_tracker'
            }
        });
    };
    const handleBackToList = () => {
        setActiveView('list');
        setSelectedTracker(null);
    };
    // Sorting Helper (managed from backend)
    // For clients, only show trackers where approval has been sent
    const sortedLists = isClient ? lists.filter((l) => l.hasApprovalSent || l.mail_sent) : lists;
    // Filtering Helper
    const filteredLists = useMemo(() => {
        if (!searchText.trim())
            return sortedLists;
        const q = searchText.toLowerCase();
        return sortedLists.filter((item) => item.name?.toLowerCase().includes(q) ||
            item.created_by?.name?.toLowerCase().includes(q));
    }, [sortedLists, searchText]);
    // Pagination Calculations
    const totalPages = Math.ceil(filteredLists.length / itemsPerPage);
    const paginatedLists = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredLists.slice(start, start + itemsPerPage);
    }, [filteredLists, currentPage, itemsPerPage]);
    const handleSortChange = (columnId, direction) => {
        setSortBy(columnId);
        setSortOrder(direction);
    };
    // Columns Configuration
    const columns = [
        { id: 'name', label: 'Tracker Name', width: "18%", sortable: true, align: 'left' },
        { id: 'created_by', label: 'Created By', sortable: true, align: 'left' },
        { id: 'createdAt', label: 'Creation Date', sortable: true, align: 'left' },
        { id: 'status', label: 'Status', sortable: true, align: 'left' },
        { id: 'contacts', label: 'Contacts', sortable: true, align: 'left' },
        { id: 'action', label: 'Action', align: 'left' }
    ];
    // Status Pill Renderer
    const renderStatusPill = (status) => {
        let bgcolor = '#F2F4F7';
        let color = '#344054';
        if (status === 'Completed') {
            bgcolor = '#ECFDF3';
            color = '#027A48';
        }
        else if (status === 'In-Progress') {
            bgcolor = '#F9F5FF';
            color = '#6941C6';
        }
        return (_jsx(Box, { sx: {
                display: 'inline-flex',
                px: '8px',
                py: '2px',
                borderRadius: '16px',
                bgcolor,
                color,
                fontSize: '12px',
                fontWeight: 500
            }, children: status }));
    };
    // Contacts Count Pill Renderer
    const renderContactsValue = (contacts) => {
        const total = contacts?.total || 0;
        const synced = contacts?.synced || 0;
        const duplicate = contacts?.duplicate || 0;
        return (_jsxs(Box, { sx: {
                display: 'inline-flex',
                alignItems: 'center',
                px: '10px',
                py: '4px',
                borderRadius: '16px',
                bgcolor: '#F9F5FF',
                color: '#7F56D9',
                fontSize: '12px',
                fontWeight: 500
            }, children: [_jsx(HoverTooltip, { title: "All contacts - (contacts without primary email + duplicate contacts)", children: _jsx("span", { style: { cursor: 'help' }, children: total }) }), _jsx("span", { style: { margin: '0 2px' }, children: "/" }), _jsx(HoverTooltip, { title: "Total contacts synced to Hubspot", children: _jsx("span", { style: { cursor: 'help' }, children: synced }) }), _jsx("span", { style: { marginLeft: '6px' } }), _jsx(HoverTooltip, { title: "Total no. of contacts with repeating primary email.", children: _jsxs("span", { style: { cursor: 'help' }, children: ["(", duplicate, ")"] }) })] }));
    };
    // Main Cell Renderer
    const renderCell = (row, column) => {
        switch (column.id) {
            case 'name': {
                const name = row.name || '';
                const isTruncated = name.length > 25;
                const displayName = isTruncated ? `${name.substring(0, 25)}...` : name;
                const cellContent = (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', fontWeight: 400, color: '#101828', cursor: isTruncated ? 'help' : 'default' }, children: displayName }));
                return isTruncated ? (_jsx(HoverTooltip, { title: name, children: _jsx(Box, { sx: { display: 'inline-block' }, children: cellContent }) })) : (cellContent);
            }
            case 'created_by':
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#475467' }, children: row.created_by?.name || 'System' }));
            case 'createdAt':
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#475467' }, children: fmtDate(row.createdAt) }));
            case 'status':
                return renderStatusPill(row.status);
            case 'contacts':
                return renderContactsValue(row.contacts);
            case 'action':
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px' }, children: [_jsx(IconButton, { size: "small", onClick: () => handleViewDetails(row), sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "View Contacts", children: _jsx("img", { src: "/assets/eye.svg", alt: "View", style: { width: 16, height: 16 } }) }), _jsxs(Box, { sx: { position: 'relative', display: 'inline-flex' }, children: [_jsx(IconButton, { size: "small", onClick: () => handleNotesClick(row), sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "Tracker Sheet Notes", children: _jsx("img", { src: "/assets/book.svg", alt: "Note", style: { width: 16, height: 16 } }) }), row.notes && (_jsx(Box, { sx: {
                                        position: 'absolute',
                                        top: '4px',
                                        right: '4px',
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        bgcolor: '#F04438',
                                        border: '1px solid #fff'
                                    } }))] }), (hasManageTrackersAccess || (!isClient && hasClientApprovalAccess)) && (_jsx(IconButton, { size: "small", onClick: (e) => handleMenuOpen(e, row), sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "Actions Menu", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [_jsx("circle", { cx: "8", cy: "4", r: "1.5", fill: "#667085" }), _jsx("circle", { cx: "8", cy: "8", r: "1.5", fill: "#667085" }), _jsx("circle", { cx: "8", cy: "12", r: "1.5", fill: "#667085" })] }) }))] }));
            default:
                return null;
        }
    };
    return (_jsxs(_Fragment, { children: [activeView === 'list' ? (
            // ══════════════════════ LIST VIEW ══════════════════════
            _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: _jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #D0D5DD', overflow: 'hidden' }, className: "table-scroll-container", children: [_jsx(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'end',
                                borderBottom: '1px solid #EAECF0',
                                flexWrap: 'wrap',
                                gap: '12px',
                                px: 1
                            }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }, children: [_jsx(TableToolbar, { title: "", showSearch: true, searchValue: searchText, onSearchChange: setSearchText, showRows: true, showExpand: true, showFullScreen: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), searchPlaceholder: "Search trackers..." }), !isClient && hasManageTrackersAccess && (_jsx(Button, { variant: "outlined", startIcon: _jsx(CloudUpload, { size: 16 }), onClick: handleUploadButtonClick, className: "all-Font-Sizes", sx: {
                                            color: '#344054',
                                            borderColor: '#D0D5DD',
                                            textTransform: 'none',
                                            height: '40px',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            '&:hover': {
                                                borderColor: '#98A2B3',
                                                bgcolor: '#F9FAFB'
                                            }
                                        }, children: "Upload Tracker" })), !isClient && hasManageTrackersAccess && (_jsx(Button, { variant: "contained", startIcon: _jsx(Plus, { size: 16 }), onClick: () => navigate(ROUTES.addOutreachTracker.path, { state: { clientName, from: 'publisher_tracker' } }), className: "all-Font-Sizes", sx: {
                                            bgcolor: '#EE2A7B',
                                            color: '#fff',
                                            textTransform: 'none',
                                            height: '40px',
                                            borderRadius: '8px',
                                            fontWeight: 600,
                                            '&:hover': {
                                                bgcolor: '#D91F6A'
                                            }
                                        }, children: "Add Tracker" }))] }) }), _jsx(CommonTable, { columns: columns, rows: paginatedLists, renderCell: renderCell, isLoading: loading || actionLoading, isFirstLoad: loading && lists.length === 0, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (colId, dir) => handleSortChange(colId, dir || 'desc'), expandTrigger: "none", stickyHeader: true, maxHeight: "calc(100vh - 435px)", hiddenColumns: hiddenColumns, onColumnVisibilityChange: (newHidden) => setHiddenColumns(newHidden), columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnVisibilityConfig: {
                                enable: true,
                                columns: ['name', 'created_by', 'createdAt', 'contacts', 'status', 'action'],
                                enableReorder: true
                            } }), _jsx(Box, { sx: { p: '12px 16px', borderTop: '1px solid #EAECF0' }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, totalEntries: filteredLists.length, itemsPerPage: itemsPerPage, onPageChange: setCurrentPage, showEntriesMessage: true }) })] }) })) : (
            // ══════════════════════ DETAILED CONTACTS VIEW ══════════════════════
            _jsx(Box, { sx: { p: 3 }, children: _jsx(Typography, { children: "Details view disabled." }) })), Boolean(menuAnchor) && (_jsx(Portal, { children: _jsx(Box, { onClick: handleMenuClose, sx: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(0, 0, 0, 0.15)',
                        zIndex: 1400
                    } }) })), _jsxs(Menu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: handleMenuClose, disableScrollLock: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, sx: {
                    zIndex: 1500,
                    '& .MuiPaper-root': {
                        borderRadius: '8px',
                        boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
                        border: '1px solid #EAECF0',
                        mt: 0.5,
                        minWidth: '180px'
                    }
                }, children: [hasManageTrackersAccess && (_jsxs(MenuItem, { onClick: handleEditClick, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(Edit, { size: 16 }), "Edit Tracker"] })), !isClient && hasClientApprovalAccess && (activeMenuTracker?.hasApprovalSent ? (_jsxs(MenuItem, { onClick: handleSendApproval, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(Send, { size: 16 }), "Resend For Approval"] })) : (_jsxs(MenuItem, { onClick: handleSendApproval, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(Send, { size: 16 }), "Send For Approval"] }))), !isClient && hasManageTrackersAccess && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { height: '1px', bgcolor: '#EAECF0', my: 0.5 } }), _jsxs(MenuItem, { onClick: handleDeleteClick, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#D92D20' }, children: [_jsx(Trash2, { size: 16 }), "Delete Tracker"] })] }))] }), _jsx(UploadTrackerDrawer, { open: uploadDrawerOpen, onClose: () => setUploadDrawerOpen(false), uploadFile: uploadFile, setUploadFile: setUploadFile, onSubmit: handleExcelUploadSubmit, actionLoading: actionLoading }), _jsx(NotesModal, { open: notesModalOpen, onClose: () => setNotesModalOpen(false), initialNotes: notesValue, onSave: handleNotesSave, actionLoading: actionLoading }), _jsx(ConfirmActionModal, { open: deleteModalOpen, onClose: () => setDeleteModalOpen(false), onConfirm: confirmDeleteTracker, title: "Delete Tracker List?", description: `Are you sure you want to delete "${activeMenuTracker?.name}"? This will soft delete all associated contacts. This action is irreversible.`, confirmText: "Delete List", cancelText: "Cancel", loading: actionLoading })] }));
}
